import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Factory, UtensilsCrossed, CheckCircle, ArrowRight, Snowflake, Filter, ShieldCheck, Droplets, Layers, ClipboardCheck, ChevronDown } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const SERVICES = [
  {
    id: "manufacturers",
    icon: <Factory size={32} />,
    title: "For Food Manufacturers",
    subtitle: "Bulk Supply & Custom Formulations",
    desc: "We supply frozen fruits, pulps, and vegetables in industrial quantities to food manufacturers across India. Our products are ideal for large-scale production of ice creams, beverages, dairy products, bakery items, and more.",
    features: [
      "Bulk quantities from 25 kg to multi-ton orders",
      "Custom brix levels and formulations",
      "Consistent quality across every batch",
      "Aseptic and industrial packaging options",
      "Dedicated account management",
      "Flexible payment terms for regular clients"
    ],
    color: "#2B7A1E",
    bgColor: "bg-green-50"
  },
  {
    id: "horeca",
    icon: <UtensilsCrossed size={32} />,
    title: "For HORECA",
    subtitle: "Hotels, Restaurants & Catering",
    desc: "Ready-to-use frozen ingredients that save prep time without compromising on quality. From five-star hotels to cloud kitchens, our products help chefs deliver consistent results every service.",
    features: [
      "Ready-to-use IQF fruits and vegetables",
      "Smaller pack sizes for kitchen convenience",
      "Consistent taste and texture in every delivery",
      "Cold-chain delivery to your kitchen door",
      "Wide range of seasonal fruits available year-round",
      "Custom portion sizes available"
    ],
    color: "#D94F04",
    bgColor: "bg-orange-50"
  },
];

const PROCESS_STEPS = [
  {
    step: 1, icon: Filter,
    title: "Sorting & Cleaning",
    desc: "Raw fruits are handpicked, graded by size and ripeness, then thoroughly washed with food-grade water to remove all surface impurities."
  },
  {
    step: 2, icon: ShieldCheck,
    title: "Metal Detection",
    desc: "All produce passes through advanced metal detection systems to ensure zero contamination and full food safety compliance before processing."
  },
  {
    step: 3, icon: Droplets,
    title: "Extraction & Treatment",
    desc: "Pulp extraction and enzymatic treatment carried out under hygienic, controlled conditions to preserve natural flavour, colour and nutrition."
  },
  {
    step: 4, icon: Layers,
    title: "Blending & Filling",
    desc: "Products are blended to precise brix levels and filled into sterile packaging — aseptic pouches or bulk drums as per order requirements."
  },
  {
    step: 5, icon: Snowflake,
    title: "Freezing",
    desc: "IQF or blast freezing at -35°C to -40°C locks in freshness, nutrition, colour and peak flavour at the moment of optimal ripeness."
  },
  {
    step: 6, icon: ClipboardCheck,
    title: "Quality Testing",
    desc: "Every batch undergoes rigorous microbiological, chemical and sensory testing before release — fully FSSAI & ISO 22000 compliant."
  },
];

export default function Services() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <div className="pt-20" data-testid="services-page">
      {/* Hero — with parallax background */}
      <div ref={heroRef} className="relative overflow-hidden">
        <motion.img
          src="https://images.unsplash.com/photo-1542838132-92c53300491e?w=1600&q=80"
          alt=""
          aria-hidden="true"
          style={{ y: imageY }}
          className="absolute inset-0 w-full h-[150%] object-cover opacity-[0.13] scale-110"
        />
        <section className="py-12 md:py-16 bg-[#f8f8f8]/80">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 relative z-10">
            <Badge className="bg-green-100 text-[#2B7A1E] hover:bg-green-100 mb-4 px-4 py-1.5 text-xs font-semibold tracking-wide rounded-full border-0">
              Services
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#333] tracking-tight mb-3" style={{ fontFamily: 'Montserrat' }}>
              Solutions for Every<br />Food Business
            </h1>
            <p className="text-gray-600 max-w-xl">
              Tailored frozen food supply solutions for manufacturers and HORECA businesses.
            </p>
          </div>
        </section>
      </div>

      {/* Services */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 space-y-20">
          {SERVICES.map((service, idx) => (
            <motion.div
              key={service.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={stagger}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${idx % 2 === 1 ? "lg:grid-flow-col-dense" : ""}`}
              data-testid={`service-${service.id}`}
            >
              <motion.div variants={fadeUp} className={idx % 2 === 1 ? "lg:col-start-2" : ""}>
                <div className={`w-16 h-16 rounded-2xl ${service.bgColor} flex items-center justify-center mb-6`} style={{ color: service.color }}>
                  {service.icon}
                </div>
                <h2 className="text-3xl font-bold text-[#333] tracking-tight mb-2" style={{ fontFamily: 'Montserrat' }}>
                  {service.title}
                </h2>
                <p className="text-base font-medium mb-4" style={{ color: service.color }}>{service.subtitle}</p>
                <p className="text-gray-600 leading-relaxed mb-6">{service.desc}</p>

                <ul className="space-y-3 mb-8">
                  {service.features.map((feat) => (
                    <li key={feat} className="flex gap-3">
                      <CheckCircle size={18} className="flex-shrink-0 mt-0.5" style={{ color: service.color }} />
                      <span className="text-sm text-gray-600">{feat}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/contact">
                  <Button
                    className="rounded-full font-semibold px-8 h-11 text-white shadow-lg"
                    style={{ backgroundColor: service.color }}
                    data-testid={`service-cta-${service.id}`}
                  >
                    Get Started <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </motion.div>

              <motion.div variants={fadeUp} className={idx % 2 === 1 ? "lg:col-start-1" : ""}>
                <div className={`rounded-3xl p-8 md:p-12 ${service.bgColor} relative overflow-hidden`}>
                  <div className="grid grid-cols-2 gap-4">
                    {service.features.slice(0, 4).map((feat, i) => (
                      <div key={i} className="bg-white rounded-2xl p-4 shadow-sm">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white mb-2 text-sm font-bold" style={{ backgroundColor: service.color }}>
                          {i + 1}
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">{feat}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Cold Storage Facility */}
      <section className="py-16 md:py-24 bg-white" data-testid="storage-facility-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
          >
            {/* Left — text content */}
            <motion.div variants={fadeUp}>
              <div className="w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center mb-6 text-[#2B7A1E]">
                <Snowflake size={32} />
              </div>
              <h2 className="text-3xl font-bold text-[#333] tracking-tight mb-2" style={{ fontFamily: 'Montserrat' }}>
                Cold Storage Facility
              </h2>
              <p className="text-base font-medium mb-4 text-[#2B7A1E]">State-of-the-art Infrastructure in Pune</p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our in-house cold storage facility ensures your produce is stored, processed, and dispatched under optimal conditions — from receipt to delivery, with zero compromise on quality.
              </p>

              <ul className="space-y-3 mb-8">
                {[
                  "Cool Room: 100 TON capacity · -18°C to 22°C",
                  "Blast Freezing: 1 TON unit · -40°C · HACCP Controlled",
                  "IQF (Individual Quick Freezing) available",
                  "Generator power backup — uninterrupted cold chain",
                  "Digital temperature monitoring with client logs",
                  "No security deposit · Bulk discount policy",
                ].map((feat) => (
                  <li key={feat} className="flex gap-3">
                    <CheckCircle size={18} className="flex-shrink-0 mt-0.5 text-[#2B7A1E]" />
                    <span className="text-sm text-gray-600">{feat}</span>
                  </li>
                ))}
              </ul>

              <Link to="/contact">
                <Button
                  className="rounded-full font-semibold px-8 h-11 text-white shadow-lg bg-[#2B7A1E] hover:bg-[#236618]"
                  data-testid="service-cta-storage"
                >
                  Enquire Now <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </motion.div>

            {/* Right — image visual */}
            <motion.div variants={fadeUp}>
              <div className="rounded-3xl p-6 md:p-8 bg-green-50 relative overflow-hidden">
                <img
                  src="/cold2.webp"
                  alt="Cold storage interior"
                  className="w-full h-52 object-cover rounded-2xl shadow-sm mb-4"
                />
                <div className="grid grid-cols-2 gap-4">
                  <img src="/cold3.webp" alt="Blast freezing unit" className="w-full h-32 object-cover rounded-2xl shadow-sm" />
                  <img src="/cold4.webp" alt="Temperature monitoring" className="w-full h-32 object-cover rounded-2xl shadow-sm" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 md:py-28 bg-[#002f47] relative overflow-hidden" data-testid="process-section">
        {/* Subtle facility background */}
        <img src="/cold1.webp" aria-hidden="true" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#002f47]/70 via-transparent to-[#002f47]/70" />

        <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8 lg:px-12">

          {/* Header */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="text-center mb-14">
            <motion.div variants={fadeUp}>
              <Badge className="bg-[#2B7A1E]/20 text-green-300 hover:bg-[#2B7A1E]/20 mb-4 px-4 py-1.5 text-xs font-semibold tracking-widest rounded-full border border-[#2B7A1E]/30">
                HOW WE WORK
              </Badge>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-3" style={{ fontFamily: 'Montserrat' }}>
              Our Processing Methodology
            </motion.h2>
            <motion.div variants={fadeUp} className="w-14 h-1 bg-[#2B7A1E] rounded-full mx-auto mb-4" />
            <motion.p variants={fadeUp} className="text-white/50 text-sm max-w-md mx-auto leading-relaxed">
              A rigorous 6-step process — from raw fruit to frozen product — ensuring consistent quality and food safety in every single batch.
            </motion.p>
          </motion.div>

          {/* Row 1: Steps 1–3 */}
          <motion.div
            initial="hidden" whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={{ visible: { transition: { staggerChildren: 0.13 } } }}
            className="relative grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3"
          >
            {/* Dashed connector line between icons (desktop) */}
            <div className="hidden lg:block absolute top-[46px] left-[calc(16.67%+22px)] right-[calc(16.67%+22px)] border-t-2 border-dashed border-[#2B7A1E]/30 pointer-events-none" />

            {PROCESS_STEPS.slice(0, 3).map((step) => {
              const Icon = step.icon;
              return (
                <motion.div key={step.step} variants={fadeUp} className="relative group">
                  <div className="h-full bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/[0.08] hover:border-[#2B7A1E]/40 transition-all duration-300 relative overflow-hidden">
                    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[#2B7A1E]/50 to-transparent" />
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-11 h-11 rounded-xl bg-[#2B7A1E] flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-green-900/30 relative z-10">
                        <Icon size={20} />
                      </div>
                      <span className="text-5xl font-black text-white/[0.04] leading-none select-none tabular-nums">{String(step.step).padStart(2, '0')}</span>
                    </div>
                    <div className="text-[10px] font-bold text-[#2B7A1E]/70 tracking-widest mb-1.5">STEP {String(step.step).padStart(2, '0')}</div>
                    <h3 className="text-sm font-bold text-white mb-2 leading-snug" style={{ fontFamily: 'Montserrat' }}>{step.title}</h3>
                    <p className="text-xs text-white/50 leading-relaxed">{step.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* U-turn snake connector */}
          <div className="hidden lg:flex items-center gap-3 my-3 text-white/20 text-[10px] tracking-widest font-medium">
            <div className="flex-1 border-t border-dashed border-white/10" />
            <ChevronDown size={13} className="text-[#2B7A1E]/50 flex-shrink-0" />
            <span className="flex-shrink-0">CONTINUES</span>
            <ChevronDown size={13} className="text-[#2B7A1E]/50 flex-shrink-0" />
            <div className="flex-1 border-t border-dashed border-white/10" />
          </div>

          {/* Row 2: Steps 4–6 */}
          <motion.div
            initial="hidden" whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={{ visible: { transition: { staggerChildren: 0.13 } } }}
            className="relative grid grid-cols-1 sm:grid-cols-3 gap-4"
          >
            {/* Dashed connector line between icons (desktop) */}
            <div className="hidden lg:block absolute top-[46px] left-[calc(16.67%+22px)] right-[calc(16.67%+22px)] border-t-2 border-dashed border-[#2B7A1E]/30 pointer-events-none" />

            {PROCESS_STEPS.slice(3, 6).map((step) => {
              const Icon = step.icon;
              const isLast = step.step === 6;
              return (
                <motion.div key={step.step} variants={fadeUp} className="relative group">
                  <div className={`h-full border rounded-2xl p-6 transition-all duration-300 relative overflow-hidden ${
                    isLast
                      ? 'bg-[#2B7A1E]/20 border-[#2B7A1E]/40 hover:bg-[#2B7A1E]/25 hover:border-[#2B7A1E]/60'
                      : 'bg-white/5 border-white/10 hover:bg-white/[0.08] hover:border-[#2B7A1E]/40'
                  }`}>
                    <div className={`absolute top-0 inset-x-0 h-[2px] ${isLast ? 'bg-[#2B7A1E]' : 'bg-gradient-to-r from-transparent via-[#2B7A1E]/50 to-transparent'}`} />
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-11 h-11 rounded-xl bg-[#2B7A1E] flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-green-900/30 relative z-10">
                        <Icon size={20} />
                      </div>
                      <span className="text-5xl font-black text-white/[0.04] leading-none select-none tabular-nums">{String(step.step).padStart(2, '0')}</span>
                    </div>
                    <div className="text-[10px] font-bold text-[#2B7A1E]/70 tracking-widest mb-1.5">STEP {String(step.step).padStart(2, '0')}</div>
                    <h3 className="text-sm font-bold text-white mb-2 leading-snug" style={{ fontFamily: 'Montserrat' }}>{step.title}</h3>
                    <p className="text-xs text-white/50 leading-relaxed">{step.desc}</p>
                    {isLast && (
                      <div className="mt-3 inline-flex items-center gap-1.5 bg-[#2B7A1E]/20 rounded-full px-2.5 py-1">
                        <CheckCircle size={11} className="text-green-400" />
                        <span className="text-[10px] font-semibold text-green-400 tracking-wide">Batch Approved</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#005A80]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Montserrat' }}>
            Let's Discuss Your Requirements
          </h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            Whether you need bulk supply or HORECA solutions - we're here to help your food business grow.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact">
              <Button className="rounded-full bg-white text-[#005A80] font-bold px-8 h-12 hover:bg-gray-100 shadow-lg" data-testid="services-cta-contact">
                Contact Our Team
              </Button>
            </Link>
            <a href="https://wa.me/919309314713?text=Hi, I need information about your services." target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-full border-2 border-white text-white font-semibold px-8 h-12 hover:bg-white/10" data-testid="services-cta-whatsapp">
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
